import {isImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {
    AnyImageOpContext,
    AnyImageOpResultNode,
    ImageOpNode,
    ImageOpOutputs,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeNormalizedCrossCorrelation} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/normalized-cross-correlation/node"
import {ImageOpNodeRasterizeGeometry} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/rasterize-geometry/node"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/find-best-match-along-segment/definition"
import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {Vector2, Vector2Like} from "@cm/math"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeTilingFindBestMatchAlongSegment"}

@registerImageOpNode
export class ImageOpNodeTilingFindBestMatchAlongSegment extends ImageOpNode<typeof definition> {
    override get definition() {
        return nodeDefinition
    }

    override dispose(): void {
        this.imageOpNodeBag.dispose()
        super.dispose()
    }

    override run = async (evaluator: Evaluator<AnyImageOpContext>): Promise<ImageOpOutputs<typeof definition>> => {
        const {imageOpHalOptions, sourceImage, referenceSegment, searchSegment, stride: coarseStride, penaltyFn} = await evaluator.getAll(this.parameters)

        this.imageOpNodeBag.beginFrame()

        const referenceSegmentOrigin = Vector2.fromVector2Like(referenceSegment.from)
        const referenceSegmentEnd = Vector2.fromVector2Like(referenceSegment.to)
        const referenceSegmentDelta = referenceSegmentEnd.sub(referenceSegmentOrigin)
        const referenceSegmentDir = referenceSegmentDelta.normalized()
        const referenceSegmentLength = referenceSegmentDelta.norm()

        const referenceSegmentDirPerp = referenceSegmentDir.perp()
        const templateWidth = Math.round(referenceSegmentLength)
        const templateHeight = Math.round(referenceSegment.width)
        const templateVertex00 = referenceSegmentOrigin.add(referenceSegmentDirPerp.mul(-templateHeight * 0.5))
        const templateVertex10 = referenceSegmentOrigin.add(referenceSegmentDir.mul(templateWidth).add(referenceSegmentDirPerp.mul(-templateHeight * 0.5)))
        const templateVertex11 = referenceSegmentOrigin.add(referenceSegmentDir.mul(templateWidth).add(referenceSegmentDirPerp.mul(templateHeight * 0.5)))
        const templateVertex01 = referenceSegmentOrigin.add(referenceSegmentDirPerp.mul(templateHeight * 0.5))
        const templateImage = this.imageOpNodeBag.getNode("RasterizeTemplate", ImageOpNodeRasterizeGeometry, {
            imageOpHalOptions,
            descriptor: {
                ...sourceImage.descriptor,
                width: templateWidth,
                height: templateHeight,
            },
            geometry: {
                topology: "triangleList",
                vertices: {
                    positions: new Float32Array([0, 0, templateWidth, 0, templateWidth, templateHeight, 0, templateHeight]),
                    uvs: new Float32Array([
                        templateVertex00.x,
                        templateVertex00.y,
                        templateVertex10.x,
                        templateVertex10.y,
                        templateVertex11.x,
                        templateVertex11.y,
                        templateVertex01.x,
                        templateVertex01.y,
                    ]),
                },
                indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
            },
            textureImage: sourceImage,
        })
        const searchSegmentOrigin = Vector2.fromVector2Like(searchSegment.from)
        const searchSegmentEnd = Vector2.fromVector2Like(searchSegment.to)
        const searchSegmentDelta = searchSegmentEnd.sub(searchSegmentOrigin)
        const searchSegmentDir = searchSegmentDelta.normalized()
        const searchSegmentDirPerp = searchSegmentDir.perp()
        const searchSegmentLength = searchSegmentDelta.norm()
        const searchImageWidth = Math.round(searchSegmentLength)
        const searchImageHeight = Math.round(searchSegment.width)
        const resultSize = {
            x: searchImageWidth - templateWidth + 1,
            y: searchImageHeight - templateHeight + 1,
        }
        if (resultSize.x <= 0 || resultSize.y <= 0) {
            throw new Error("Search segment must be at least as long and wide as the template")
        }
        const searchVertex00 = searchSegmentOrigin.add(searchSegmentDirPerp.mul(-searchImageHeight * 0.5))
        const searchVertex10 = searchSegmentOrigin.add(searchSegmentDir.mul(searchImageWidth).add(searchSegmentDirPerp.mul(-searchImageHeight * 0.5)))
        const searchVertex11 = searchSegmentOrigin.add(searchSegmentDir.mul(searchImageWidth).add(searchSegmentDirPerp.mul(searchImageHeight * 0.5)))
        const searchVertex01 = searchSegmentOrigin.add(searchSegmentDirPerp.mul(searchImageHeight * 0.5))
        const searchImage = this.imageOpNodeBag.getNode("rasterizeSearch", ImageOpNodeRasterizeGeometry, {
            imageOpHalOptions,
            descriptor: {
                ...sourceImage.descriptor,
                width: searchImageWidth,
                height: searchImageHeight,
            },
            geometry: {
                topology: "triangleList",
                vertices: {
                    positions: new Float32Array([0, 0, searchImageWidth, 0, searchImageWidth, searchImageHeight, 0, searchImageHeight]),
                    uvs: new Float32Array([
                        searchVertex00.x,
                        searchVertex00.y,
                        searchVertex10.x,
                        searchVertex10.y,
                        searchVertex11.x,
                        searchVertex11.y,
                        searchVertex01.x,
                        searchVertex01.y,
                    ]),
                },
                indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
            },
            textureImage: sourceImage,
        })
        const computePositionFromIndex = (index: Vector2Like) =>
            searchSegmentOrigin.add(searchSegmentDir.mul(index.x).add(searchSegmentDirPerp.mul(index.y - (searchImageHeight - templateHeight) * 0.5)))

        const runNcc = async (bagKey: string, stride: number, sourceRegionX: number, sourceRegionWidth: number) => {
            const expectedResultWidth = Math.floor((sourceRegionWidth - templateWidth) / stride) + 1
            const correlationNode: AnyImageOpResultNode = this.imageOpNodeBag.getNode(bagKey, ImageOpNodeNormalizedCrossCorrelation, {
                imageOpHalOptions,
                sourceImage: searchImage,
                templateImage: templateImage,
                sourceRegion: {x: sourceRegionX, y: 0, width: sourceRegionWidth, height: searchImageHeight},
                stride: {x: stride, y: 1},
            })
            const correlation = await evaluator.get(correlationNode)
            if (!isImageOpHal(correlation)) {
                throw new Error("Expected correlation to be an ImageOpHal")
            }
            if (correlation.descriptor.width !== expectedResultWidth || correlation.descriptor.height !== resultSize.y) {
                throw new Error("Unexpected correlation image size")
            }
            const updatedCorrelation = await correlation.getUpdatedHalImage({
                x: 0,
                y: 0,
                width: correlation.descriptor.width,
                height: correlation.descriptor.height,
                mipLevel: 0,
            })
            const data = updatedCorrelation.halImage.readImageDataFloat()
            updatedCorrelation.release()
            return {data, resultWidth: expectedResultWidth}
        }

        const findPeak = (data: Float32Array, resultWidth: number, stride: number, originX: number) => {
            let peakIndex = {x: 0, y: 0}
            let peakValue = Number.NEGATIVE_INFINITY
            for (let y = 0; y < resultSize.y; y++) {
                for (let x = 0; x < resultWidth; x++) {
                    const origX = originX + x * stride
                    const penalty = penaltyFn ? penaltyFn(computePositionFromIndex({x: origX, y})) : 0
                    const value = data[y * resultWidth + x] - penalty
                    if (value > peakValue) {
                        peakIndex = {x: origX, y}
                        peakValue = value
                    }
                }
            }
            return {peakIndex, peakValue}
        }

        const {data: coarseData, resultWidth: coarseResultWidth} = await runNcc("NCCCoarse", coarseStride, 0, searchImageWidth)
        const {peakIndex: coarsePeakIndex, peakValue: coarsePeakValue} = findPeak(coarseData, coarseResultWidth, coarseStride, 0)

        let peakIndex: Vector2Like
        let peakValue: number
        if (coarseStride === 1) {
            peakIndex = coarsePeakIndex
            peakValue = coarsePeakValue
        } else {
            const fineRegionX = Math.max(0, coarsePeakIndex.x - coarseStride)
            const fineRegionRight = Math.min(searchImageWidth, coarsePeakIndex.x + coarseStride + templateWidth)
            const {data: fineData, resultWidth: fineResultWidth} = await runNcc("NCCFine", 1, fineRegionX, fineRegionRight - fineRegionX)
            const {peakIndex: finePeakIndex, peakValue: finePeakValue} = findPeak(fineData, fineResultWidth, 1, fineRegionX)
            peakIndex = finePeakIndex
            peakValue = finePeakValue
        }

        // console.log(`Best peak along line at (${peakIndex.x}, ${peakIndex.y}) with value ${peakValue}`)
        const bestMatchPosition = computePositionFromIndex(peakIndex)

        this.imageOpNodeBag.endFrame()

        return {
            correlationData: coarseData as Float32Array<ArrayBuffer>,
            peakIndex,
            peakValue,
            bestMatchPosition,
        }
    }

    private imageOpNodeBag = new ImageOpNodeBag("ImageOpNodeTilingFindBestMatchAlongSegment")
}
